import { User } from 'firebase/auth';
import { useRecoilValue } from 'recoil';
import { sessionExpiredModalState } from '../../recoil/sessionModal/sessionModal.atom';
import { useCallback, useEffect, useRef } from 'react';
import { verifyIdToken } from '../../service/authentication';

export const usePingTokenOnExpire = (
  user: User | null | undefined,
  initialExpireTime: number | null | undefined
) => {
  const expiredModalOpen = useRecoilValue(sessionExpiredModalState);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTriggered = useRef<boolean>(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const schedulePing = useCallback(
    (expireInSec: number | null | undefined) => {
      if (!user || !expireInSec || expireInSec <= 0) return;

      clearTimer();

      if (expiredModalOpen) return;

      timerRef.current = setTimeout(async () => {
        try {
          if (isTriggered.current) return;
          isTriggered.current = true;

          const { expires_in } = await verifyIdToken(user);
          isTriggered.current = false;
          if (typeof expires_in === 'number' && expires_in > 0) schedulePing(expires_in);
        } catch {
          isTriggered.current = false;
        }
      }, Math.max(0, expireInSec * 1000));
    },
    [user, expiredModalOpen, clearTimer]
  );

  const reSyncPing = useCallback(async () => {
    if (!user || expiredModalOpen) return;
    try {
      if (isTriggered.current) return;
      isTriggered.current = true;

      const { expires_in } = await verifyIdToken(user);
      isTriggered.current = false;

      schedulePing(expires_in);
    } catch {
      isTriggered.current = false;
    }
  }, [user, expiredModalOpen, schedulePing]);

  useEffect(() => {
    schedulePing(initialExpireTime);

    return () => clearTimer();
  }, [initialExpireTime, schedulePing, clearTimer]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') reSyncPing();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [reSyncPing]);
};
